import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  CheckCircle,
  Plane,
  Anchor,
  AlertTriangle,
  Clock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ProductPageNav, ProductPageFooter } from '@/components/ProductPageNav'
import { SEOHead } from '@/components/SEOHead'

// ── SVG components ──────────────────────────────────────────────────────────

function FlightPathSVG() {
  // Background grid every 50px on a 700x300 canvas
  const gridLines = []
  for (let x = 0; x <= 700; x += 50) {
    gridLines.push(<line key={`gx-${x}`} x1={x} y1="0" x2={x} y2="300" stroke="#0d1a0d" strokeWidth="0.8" />)
  }
  for (let y = 0; y <= 300; y += 50) {
    gridLines.push(<line key={`gy-${y}`} x1="0" y1={y} x2="700" y2={y} stroke="#0d1a0d" strokeWidth="0.8" />)
  }

  // Aircraft arrow triangle helper (pointing roughly in direction of motion)
  // dir: 'ne' | 'se' | 'e' | 'sw' | 'nw'
  const aircraft = (cx: number, cy: number, dir: string, color: string) => {
    const pts: Record<string, string> = {
      ne:  `${cx},${cy - 7} ${cx + 7},${cy + 5} ${cx - 7},${cy + 5}`,
      se:  `${cx},${cy + 7} ${cx + 7},${cy - 5} ${cx - 7},${cy - 5}`,
      e:   `${cx + 7},${cy} ${cx - 5},${cy - 6} ${cx - 5},${cy + 6}`,
      sw:  `${cx},${cy + 7} ${cx - 7},${cy - 5} ${cx + 7},${cy - 5}`,
      nw:  `${cx - 7},${cy} ${cx + 5},${cy - 6} ${cx + 5},${cy + 6}`,
    }
    return <polygon points={pts[dir] ?? pts['e']} fill={color} opacity="0.95" />
  }

  // Tooltip box
  const tooltip = (x: number, y: number, lines: string[], color: string) => (
    <g>
      <rect x={x} y={y} width="190" height={14 + lines.length * 14} rx="5"
        fill="#0a0f0a" stroke={color} strokeWidth="1.2" />
      {lines.map((l, i) => (
        <text key={i} x={x + 8} y={y + 13 + i * 14} fill={i === 0 ? color : '#94a3b8'} fontSize="9" fontWeight={i === 0 ? '700' : '400'}>{l}</text>
      ))}
    </g>
  )

  return (
    <svg viewBox="0 0 700 300" className="w-full max-w-3xl mx-auto rounded-xl" style={{ background: '#060d0d' }} aria-label="Flight path visualization">
      {gridLines}

      {/* Flight path 1 — civil green — top arc */}
      <path d="M 40 80 Q 200 20 380 65" fill="none" stroke="#22c55e" strokeWidth="2" />
      <polygon points="380,65 370,56 372,68" fill="#22c55e" />
      {aircraft(330, 52, 'ne', '#22c55e')}

      {/* Flight path 2 — civil green — mid low */}
      <path d="M 60 220 Q 220 250 430 200" fill="none" stroke="#22c55e" strokeWidth="2" />
      <polygon points="430,200 418,194 420,206" fill="#22c55e" />
      {aircraft(310, 236, 'e', '#22c55e')}

      {/* Flight path 3 — blue — private/business */}
      <path d="M 100 145 Q 300 100 520 155" fill="none" stroke="#3b82f6" strokeWidth="2" />
      <polygon points="520,155 508,148 510,160" fill="#3b82f6" />
      {aircraft(390, 122, 'e', '#3b82f6')}
      {tooltip(500, 60, ['MIL-7734 | C-17 Globemaster', 'ALT: 28,000ft | Military', 'ICAO: AE1234 | Squawk: 2200'], '#ef4444')}
      <line x1="520" y1="62" x2="520" y2="155" stroke="#ef444460" strokeWidth="1" strokeDasharray="4 3" />

      {/* Flight path 4 — red — military flagged */}
      <path d="M 80 40 Q 280 -10 560 55" fill="none" stroke="#ef4444" strokeWidth="2.5" />
      <polygon points="560,55 546,46 548,60" fill="#ef4444" />
      {aircraft(450, 28, 'ne', '#ef4444')}
      {/* Red alert ring */}
      <circle cx="450" cy="28" r="14" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4 2" opacity="0.7" />

      {/* Flight path 5 — amber — anomaly */}
      <path d="M 30 260 Q 100 130 240 200 Q 380 265 580 245" fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="8 4" />
      <polygon points="580,245 567,238 568,250" fill="#f59e0b" />
      {aircraft(340, 252, 'e', '#f59e0b')}
      {/* Anomaly badge */}
      <rect x="300" y="260" width="80" height="16" rx="4" fill="#0a0f0a" stroke="#f59e0b" strokeWidth="1" />
      <text x="340" y="272" textAnchor="middle" fill="#f59e0b" fontSize="8" fontWeight="700">ANOMALY</text>

      {/* Legend box bottom-right */}
      <rect x="544" y="210" width="148" height="82" rx="6" fill="#0a0f0a" stroke="#1e3a1e" strokeWidth="1" />
      <text x="556" y="225" fill="#94a3b8" fontSize="10" fontWeight="600">Legend</text>
      {[
        { color: '#22c55e', label: 'Civil', y: 240 },
        { color: '#3b82f6', label: 'Private / Business', y: 254 },
        { color: '#ef4444', label: 'Military', y: 268 },
        { color: '#f59e0b', label: 'Anomaly detected', y: 282 },
      ].map((l) => (
        <g key={l.label}>
          <line x1="556" y1={l.y - 3} x2="570" y2={l.y - 3} stroke={l.color} strokeWidth="2.5" />
          <text x="576" y={l.y} fill="#94a3b8" fontSize="8">{l.label}</text>
        </g>
      ))}
    </svg>
  )
}

function MilitaryDetectionSVG() {
  const box = (x: number, y: number, w: number, h: number, label: string, sub: string, color: string, shape: 'rect' | 'diamond' = 'rect') => {
    if (shape === 'diamond') {
      const cx = x + w / 2
      const cy = y + h / 2
      const hw = w / 2
      const hh = h / 2
      return (
        <g>
          <polygon points={`${cx},${cy - hh} ${cx + hw},${cy} ${cx},${cy + hh} ${cx - hw},${cy}`}
            fill="#0d1117" stroke={color} strokeWidth="1.5" />
          <text x={cx} y={cy - 2} textAnchor="middle" fill={color} fontSize="9" fontWeight="700">{label}</text>
          {sub && <text x={cx} y={cy + 10} textAnchor="middle" fill="#64748b" fontSize="8">{sub}</text>}
        </g>
      )
    }
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} rx="6" fill="#0d1117" stroke={color} strokeWidth="1.5" />
        <text x={x + w / 2} y={y + h / 2 - 2} textAnchor="middle" fill={color} fontSize="9" fontWeight="700">{label}</text>
        {sub && <text x={x + w / 2} y={y + h / 2 + 10} textAnchor="middle" fill="#64748b" fontSize="8">{sub}</text>}
      </g>
    )
  }

  const arrow = (x1: number, y1: number, x2: number, y2: number, label?: string, color = '#334155') => (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.5"
        markerEnd={`url(#arrow-${color.replace('#', '')})`} />
      {label && (
        <text x={(x1 + x2) / 2 + 4} y={(y1 + y2) / 2 - 4} fill="#64748b" fontSize="8">{label}</text>
      )}
    </g>
  )

  return (
    <svg viewBox="0 0 580 180" className="w-full max-w-2xl mx-auto" aria-label="Military detection flow">
      <defs>
        <marker id="arrow-334155" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill="#334155" />
        </marker>
        <marker id="arrow-22c55e" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill="#22c55e" />
        </marker>
        <marker id="arrow-ef4444" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill="#ef4444" />
        </marker>
        <marker id="arrow-f59e0b" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill="#f59e0b" />
        </marker>
      </defs>

      {/* Node 1: Aircraft Signal */}
      {box(10, 68, 100, 44, 'Aircraft Signal', 'ADS-B / Mode-S', '#06b6d4')}

      {/* Arrow to ICAO Check */}
      {arrow(110, 90, 148, 90)}

      {/* Node 2: ICAO Range Check (diamond) */}
      {box(148, 64, 100, 52, 'ICAO Range', 'Check', '#94a3b8', 'diamond')}

      {/* Branch 1: Civil Range — left-ish down */}
      {arrow(198, 116, 198, 140, undefined, '#22c55e')}
      <text x="205" y="134" fill="#22c55e" fontSize="8">Civil</text>
      {box(155, 140, 86, 32, 'Civil Aircraft', '', '#22c55e')}

      {/* Branch 2: Military Range — right and up */}
      {arrow(248, 90, 296, 90, 'Military Range', '#ef4444')}

      {/* Node 3: Callsign Pattern (diamond) */}
      {box(296, 64, 100, 52, 'Callsign', 'Pattern Match', '#f59e0b', 'diamond')}

      {/* Branch from callsign — MIL Pattern → FLAGGED */}
      {arrow(346, 116, 346, 140, undefined, '#ef4444')}
      <text x="352" y="134" fill="#ef4444" fontSize="8">MIL pattern</text>
      {box(296, 140, 100, 32, 'FLAGGED MILITARY', '', '#ef4444')}

      {/* Branch 3: Unknown from ICAO */}
      {arrow(198, 64, 198, 42, undefined, '#f59e0b')}
      <text x="205" y="38" fill="#f59e0b" fontSize="8">Unknown</text>
      {box(148, 10, 100, 28, 'Squawk Mode', '', '#f59e0b')}

      {/* Squawk → Verify */}
      {arrow(248, 24, 296, 24, undefined, '#f59e0b')}
      {box(296, 10, 86, 28, 'VERIFY', '', '#f59e0b')}

      {/* No match from callsign → Civil */}
      {arrow(396, 90, 450, 90, 'No MIL pattern', '#22c55e')}
      {box(450, 68, 80, 44, 'Civil', 'Confirmed', '#22c55e')}

      {/* ADS-B label on right */}
      <rect x="490" y="140" width="82" height="32" rx="5" fill="#0d1117" stroke="#06b6d440" strokeWidth="1" />
      <text x="531" y="153" textAnchor="middle" fill="#06b6d4" fontSize="9" fontWeight="700">ADS-B</text>
      <text x="531" y="165" textAnchor="middle" fill="#64748b" fontSize="8">Source feed</text>
    </svg>
  )
}

function AISVesselSVG() {
  // Vessel shape: simplified triangle
  const vessel = (cx: number, cy: number, color: string, size = 1, hasAnomaly = false, trackPoints?: [number, number][]) => (
    <g>
      {trackPoints && trackPoints.map(([tx, ty], i) => (
        <circle key={i} cx={tx} cy={ty} r="2" fill={color} opacity={0.2 + i * 0.15} />
      ))}
      {trackPoints && trackPoints.length > 1 && trackPoints.map((pt, i) => {
        if (i === 0) return null
        const prev = trackPoints[i - 1]
        return <line key={`tr-${i}`} x1={prev[0]} y1={prev[1]} x2={pt[0]} y2={pt[1]}
          stroke={color} strokeWidth="0.8" opacity={0.3 + i * 0.1} />
      })}
      {hasAnomaly && (
        <>
          <circle cx={cx} cy={cy} r={size * 18 + 8} fill="none" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4 2" opacity="0.8" />
          <circle cx={cx} cy={cy} r={size * 18 + 16} fill="none" stroke="#ef4444" strokeWidth="0.8" strokeDasharray="4 2" opacity="0.4" />
        </>
      )}
      <polygon
        points={`${cx},${cy - size * 10} ${cx + size * 7},${cy + size * 8} ${cx},${cy + size * 4} ${cx - size * 7},${cy + size * 8}`}
        fill="#0d1117" stroke={color} strokeWidth="1.5"
      />
    </g>
  )

  return (
    <svg viewBox="0 0 640 260" className="w-full max-w-2xl mx-auto rounded-xl" style={{ background: '#060d0d' }} aria-label="AIS vessel tracking">
      {/* Ocean texture — subtle wave lines */}
      {[30, 80, 130, 180, 230].map(y => (
        <path key={y} d={`M 0 ${y} Q 80 ${y - 6} 160 ${y} Q 240 ${y + 6} 320 ${y} Q 400 ${y - 6} 480 ${y} Q 560 ${y + 6} 640 ${y}`}
          fill="none" stroke="#0a1a1a" strokeWidth="1" />
      ))}

      {/* Tanker — amber */}
      {vessel(80, 55, '#f59e0b', 1.1, false, [[40, 70], [55, 65], [68, 59]])}
      <text x="80" y="83" textAnchor="middle" fill="#f59e0b" fontSize="8">TANKER</text>

      {/* Cargo 1 — green */}
      {vessel(180, 100, '#22c55e', 1.0, false, [[140, 115], [155, 110], [168, 104]])}
      <text x="180" y="127" textAnchor="middle" fill="#22c55e" fontSize="8">CARGO</text>

      {/* Military — red */}
      {vessel(320, 60, '#ef4444', 0.9, false, [[270, 72], [290, 68], [308, 63]])}
      <text x="320" y="84" textAnchor="middle" fill="#ef4444" fontSize="8">MILITARY</text>
      <circle cx="320" cy="60" r="20" fill="none" stroke="#ef4444" strokeWidth="1" strokeDasharray="3 2" opacity="0.5" />

      {/* Fishing — cyan */}
      {vessel(480, 130, '#06b6d4', 0.9, false, [[445, 145], [460, 140], [470, 134]])}
      <text x="480" y="154" textAnchor="middle" fill="#06b6d4" fontSize="8">FISHING</text>

      {/* Cargo 2 — green */}
      {vessel(560, 80, '#22c55e', 0.8, false, [[530, 92], [542, 88], [552, 83]])}
      <text x="560" y="102" textAnchor="middle" fill="#22c55e" fontSize="8">CARGO</text>

      {/* Tanker 2 — amber */}
      {vessel(250, 185, '#f59e0b', 0.9, false, [[210, 197], [226, 192], [238, 188]])}
      <text x="250" y="208" textAnchor="middle" fill="#f59e0b" fontSize="8">TANKER</text>

      {/* Anomaly vessel — dark vessel, AIS gap */}
      {vessel(390, 185, '#ef4444', 1.2, true, [[340, 200], [360, 195], [375, 190]])}
      <text x="390" y="222" textAnchor="middle" fill="#ef4444" fontSize="8" fontWeight="700">DARK VESSEL</text>

      {/* Data card for anomaly vessel */}
      <rect x="408" y="145" width="155" height="62" rx="6" fill="#0a0f0a" stroke="#ef4444" strokeWidth="1.5" />
      <text x="418" y="160" fill="#ef4444" fontSize="9" fontWeight="700">DARK VESSEL</text>
      <text x="418" y="173" fill="#94a3b8" fontSize="8">Last AIS: 4h ago</text>
      <text x="418" y="186" fill="#94a3b8" fontSize="8">Gap: 240min</text>
      <text x="418" y="199" fill="#ef4444" fontSize="8" fontWeight="700">Risk: HIGH</text>
      <line x1="408" y1="180" x2="390" y2="185" stroke="#ef444480" strokeWidth="1" strokeDasharray="3 2" />

      {/* Legend */}
      <rect x="8" y="210" width="175" height="42" rx="5" fill="#0a0f0a" stroke="#1e3a1e" strokeWidth="1" />
      {[
        { color: '#f59e0b', label: 'Tanker', x: 18 },
        { color: '#22c55e', label: 'Cargo', x: 68 },
        { color: '#ef4444', label: 'Military', x: 110 },
        { color: '#06b6d4', label: 'Fishing', x: 155 },
      ].map((l) => (
        <g key={l.label}>
          <polygon points={`${l.x},${218} ${l.x + 5},${227} ${l.x},${225} ${l.x - 5},${227}`}
            fill="#0d1117" stroke={l.color} strokeWidth="1.2" />
          <text x={l.x} y="244" textAnchor="middle" fill="#94a3b8" fontSize="7">{l.label}</text>
        </g>
      ))}
    </svg>
  )
}

function AnomalyAlertSVG() {
  return (
    <svg viewBox="0 0 300 160" className="w-full max-w-sm" aria-label="Squawk alert flow">
      {/* Transponder box */}
      <rect x="10" y="55" width="80" height="50" rx="7" fill="#0d1117" stroke="#06b6d4" strokeWidth="1.5" />
      <text x="50" y="75" textAnchor="middle" fill="#06b6d4" fontSize="9" fontWeight="700">Transponder</text>
      <text x="50" y="89" textAnchor="middle" fill="#64748b" fontSize="8">Mode-S signal</text>
      <text x="50" y="101" textAnchor="middle" fill="#64748b" fontSize="8">decoded</text>

      {/* Arrow */}
      <line x1="90" y1="80" x2="120" y2="80" stroke="#334155" strokeWidth="1.5" markerEnd="url(#arr-m)" />
      <defs>
        <marker id="arr-m" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill="#334155" />
        </marker>
        <marker id="arr-r" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill="#ef4444" />
        </marker>
      </defs>

      {/* Squawk 7700 box */}
      <rect x="120" y="55" width="80" height="50" rx="7" fill="#0d1117" stroke="#f59e0b" strokeWidth="1.5" />
      <text x="160" y="75" textAnchor="middle" fill="#f59e0b" fontSize="9" fontWeight="700">Squawk 7700</text>
      <text x="160" y="89" textAnchor="middle" fill="#64748b" fontSize="8">Emergency</text>
      <text x="160" y="101" textAnchor="middle" fill="#64748b" fontSize="8">detected</text>

      {/* Arrow */}
      <line x1="200" y1="80" x2="230" y2="80" stroke="#ef4444" strokeWidth="1.5" markerEnd="url(#arr-r)" />

      {/* Alert bubble */}
      <rect x="230" y="45" width="62" height="70" rx="8" fill="#0d1117" stroke="#ef4444" strokeWidth="2" />
      {/* Pulsing ring effect */}
      <circle cx="261" cy="70" r="16" fill="none" stroke="#ef4444" strokeWidth="1.5" opacity="0.5" />
      <circle cx="261" cy="70" r="10" fill="#ef444420" stroke="#ef4444" strokeWidth="2" />
      <text x="261" y="74" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="800">!</text>
      <text x="261" y="96" textAnchor="middle" fill="#ef4444" fontSize="8" fontWeight="700">ALERT</text>
      <text x="261" y="108" textAnchor="middle" fill="#64748b" fontSize="7">Analyst</text>
      <text x="261" y="119" textAnchor="middle" fill="#64748b" fontSize="7">notified</text>

      {/* Label below */}
      <text x="150" y="135" textAnchor="middle" fill="#64748b" fontSize="9">Squawk code detection pipeline</text>
    </svg>
  )
}

function HistoricalReplaySVG() {
  const positions = [
    { x: 60,  y: 55 },
    { x: 180, y: 80 },
    { x: 300, y: 58 },
  ]

  return (
    <svg viewBox="0 0 540 130" className="w-full max-w-lg mx-auto" aria-label="Historical track replay">
      {/* Dark panel */}
      <rect x="0" y="0" width="540" height="130" rx="10" fill="#0d1117" stroke="#1e3a1e" strokeWidth="1" />

      {/* Track dashed line */}
      <path d={`M ${positions[0].x} ${positions[0].y} Q 120 100 ${positions[1].x} ${positions[1].y} Q 240 30 ${positions[2].x} ${positions[2].y}`}
        fill="none" stroke="#22c55e" strokeWidth="2" strokeDasharray="8 4" />

      {/* Position dots */}
      {positions.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="7" fill="#0d1117" stroke="#22c55e" strokeWidth="2" />
          <circle cx={p.x} cy={p.y} r="3" fill="#22c55e" />
          <text x={p.x} y={p.y - 13} textAnchor="middle" fill="#64748b" fontSize="8">
            {['T-60m', 'T-30m', 'NOW'][i]}
          </text>
        </g>
      ))}

      {/* Current position glow */}
      <circle cx={positions[2].x} cy={positions[2].y} r="14" fill="none" stroke="#22c55e" strokeWidth="1.5" opacity="0.35" />

      {/* Replay controls */}
      <rect x="20" y="95" width="500" height="25" rx="5" fill="#0a0f0a" stroke="#1e3a1e" strokeWidth="1" />
      {/* Progress bar */}
      <rect x="24" y="99" width="492" height="17" rx="4" fill="#0a0f0a" />
      <rect x="24" y="99" width="300" height="17" rx="4" fill="#22c55e18" />
      {/* Scrubber handle */}
      <rect x="314" y="97" width="6" height="21" rx="3" fill="#22c55e" />
      {/* Play button */}
      <polygon points="32,103 32,114 42,108.5" fill="#22c55e" />
      {/* Time labels */}
      <text x="50" y="110" fill="#64748b" fontSize="8">00:00</text>
      <text x="490" y="110" fill="#64748b" fontSize="8">02:30:00</text>
      <text x="280" y="110" textAnchor="middle" fill="#22c55e" fontSize="8" fontWeight="700">01:15:00</text>

      {/* Data info above */}
      <text x="350" y="22" fill="#e2e8f0" fontSize="10" fontWeight="700">Historical Track Replay</text>
      <text x="350" y="36" fill="#64748b" fontSize="8">MMSI: 338234456 | Last 6h | 3 positions shown</text>
      <text x="350" y="50" fill="#22c55e" fontSize="9">Export: KML  GeoJSON  CSV</text>
    </svg>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function AirMaritimeProduct() {
  const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
  }

  return (
    <div className="min-h-screen bg-[#0a0f0a] text-white overflow-x-hidden">
      <SEOHead
        title="Air Traffic & Maritime Intelligence — Live Flight & Vessel Tracking | Sycek OSINT"
        description="Real-time air traffic and maritime domain awareness. Live flight tracking with military vs civil classification, AIS vessel monitoring, port activity analysis, and anomaly alerts for investigators and security teams."
        keywords="air traffic intelligence, flight tracking, military aircraft tracking, maritime intelligence, AIS tracking, vessel tracking, ship monitoring, maritime domain awareness, port intelligence, flight OSINT, aircraft OSINT, maritime OSINT, OpenSky, AISStream"
        canonical="/air-maritime"
        jsonLd={{
          "@type": "Product",
          "name": "Air Traffic & Maritime Intelligence",
          "description": "Live flight tracking with military/civil classification and AIS vessel monitoring for security and investigative use.",
          "brand": { "@type": "Brand", "name": "Sycek OSINT" },
          "url": "https://sycek.io/air-maritime",
          "category": "Security Software"
        }}
      />

      {/* Background grid */}
      <div
        className="fixed inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(34,197,94,0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34,197,94,0.15) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      <ProductPageNav activePage="Air & Maritime" />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative z-10 pt-20 pb-16 px-6">
        <div className="container mx-auto max-w-5xl text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <Badge className="mb-6 bg-green-500/10 text-green-400 border border-green-500/25 text-xs uppercase tracking-widest px-4 py-1.5">
              Air & Maritime — Global Surveillance
            </Badge>
          </motion.div>

          <motion.h1
            initial="hidden" animate="visible" custom={1} variants={fadeUp}
            className="text-5xl md:text-6xl font-bold leading-tight mb-6">
            <span className="text-white">Track</span>
            <br />
            <span className="text-green-400 drop-shadow-[0_0_30px_rgba(34,197,94,0.6)]">Every Aircraft. Every Vessel.</span>
          </motion.h1>

          <motion.p
            initial="hidden" animate="visible" custom={2} variants={fadeUp}
            className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed mb-10">
            Real-time global air traffic from OpenSky Network + ADS-B feeds and maritime vessel tracking via AISStream. Military detection, dark vessel alerts, squawk code monitoring, and historical track replay in a single platform.
          </motion.p>

          <motion.div
            initial="hidden" animate="visible" custom={3} variants={fadeUp}
            className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/app/air-monitoring">
              <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-8 shadow-lg shadow-green-500/40">
                Open Air Monitoring
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/app/geoint-maritime">
              <Button size="lg" variant="outline" className="border-white/[0.15] text-slate-300 hover:text-white hover:bg-white/[0.05]">
                Maritime Tracking
                <Anchor className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Stats bar ─────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-10 border-y border-white/[0.06] bg-white/[0.02]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '20K+', label: 'Live aircraft tracked' },
              { value: '200K+', label: 'AIS vessels active' },
              { value: '<2s', label: 'Position update latency' },
              { value: 'Global', label: 'Coverage via ADS-B + AIS' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-bold text-green-400 mb-1">{s.value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Flight Path Visualization ─────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <Badge className="mb-4 bg-green-500/10 text-green-400 border border-green-500/25 text-xs uppercase tracking-widest">Air Traffic</Badge>
            <h2 className="text-3xl font-bold text-white mb-3">Live Flight Positions & Tracks</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Live aircraft positions from OpenSky Network and ADS-B feeds. Color-coded by classification: civil, private, military flagged, and anomaly detected. Real-time ICAO lookup, altitude, speed, and squawk monitoring.
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="rounded-2xl overflow-hidden border border-white/[0.06]">
            <FlightPathSVG />
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { icon: Plane,        color: 'text-green-400',  title: 'Civil Aircraft',       body: 'Commercial and general aviation tracked via OpenSky and ADS-B Exchange feeds.' },
              { icon: Plane,        color: 'text-blue-400',   title: 'Private / Business',   body: 'Business jets and private aircraft identified by ICAO hex range and registration.' },
              { icon: AlertTriangle, color: 'text-red-400',   title: 'Military Flagged',     body: 'Military aircraft detected by ICAO range, callsign pattern, and transponder mode.' },
              { icon: AlertTriangle, color: 'text-amber-400', title: 'Anomaly Detection',    body: 'Unusual flight paths, squawk alerts, and ADS-B spoofing indicators automatically flagged.' },
            ].map((card) => (
              <div key={card.title} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
                <card.icon className={`w-5 h-5 ${card.color} mb-3`} />
                <div className="text-sm font-semibold text-white mb-1">{card.title}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{card.body}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Military Detection ────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <Badge className="mb-4 bg-red-500/10 text-red-400 border border-red-500/25 text-xs uppercase tracking-widest">Military Detection</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">Classify Military Aircraft Automatically</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                A three-stage decision engine classifies every aircraft signal: ICAO hex range lookup, callsign pattern matching against military designators, and transponder mode analysis — in real time, for every aircraft on screen.
              </p>
              <ul className="space-y-3">
                {[
                  'ICAO hex range database covers 50+ military operators',
                  'Callsign regex against RCH, LAGR, JAKE, REACH, FORTE patterns',
                  'Transponder mode-S military vs civil range check',
                  'Unknown aircraft routed to squawk verification pipeline',
                  'Military aircraft near sensitive zones generate analyst alerts',
                  'ICAO lookup enriched with aircraft type (C-17, P-8, KC-135...)',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
              className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
              <MilitaryDetectionSVG />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── AIS Vessel Tracking ───────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border border-cyan-500/25 text-xs uppercase tracking-widest">Maritime AIS</Badge>
            <h2 className="text-3xl font-bold text-white mb-3">Live Vessel Positions via AISStream</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Real-time vessel positions via AISStream WebSocket. Filter by vessel type, flag state, MMSI prefix, or risk profile. Dark vessel detection via AIS gap analysis with automatic HIGH risk flagging.
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="rounded-2xl overflow-hidden border border-white/[0.06]">
            <AISVesselSVG />
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {[
              { title: 'Dark Vessel Detection',  color: 'border-red-500/25 bg-red-500/[0.03]', tColor: 'text-red-400',   desc: 'Vessels with AIS gaps exceeding configurable thresholds (15m, 1h, 4h) are automatically flagged as potential dark vessels.' },
              { title: 'Sanctioned Flag States', color: 'border-amber-500/25 bg-amber-500/[0.03]', tColor: 'text-amber-400', desc: 'MMSI prefixes cross-referenced against OFAC/EU sanctioned flag states. Instant alert on North Korea, Iran, Russia-flagged vessels.' },
              { title: 'Vessel Risk Score',      color: 'border-green-500/25 bg-green-500/[0.03]', tColor: 'text-green-400', desc: 'Composite score from AIS gap history, flag state risk, known smuggling route proximity, and previous anomaly record.' },
            ].map((card) => (
              <div key={card.title} className={`border rounded-xl p-5 ${card.color}`}>
                <div className={`text-sm font-semibold mb-2 ${card.tColor}`}>{card.title}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{card.desc}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Anomaly Detection ─────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
              className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-8">
              <AnomalyAlertSVG />
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <Badge className="mb-4 bg-amber-500/10 text-amber-400 border border-amber-500/25 text-xs uppercase tracking-widest">Anomaly Alerts</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">Real-Time Anomaly Detection</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Automatic alert generation for aviation emergencies, AIS blackouts, and unusual behavior patterns. Every alert is enriched with context and routed to analyst queues.
              </p>
              <ul className="space-y-4">
                {[
                  { code: '7500', label: 'Squawk 7500 — Hijack',          desc: 'Aircraft broadcasting hijack code. Immediate high-priority alert with position.' },
                  { code: '7600', label: 'Squawk 7600 — Comms Failure',   desc: 'Loss of radio contact. Alert includes last known heading and altitude.' },
                  { code: '7700', label: 'Squawk 7700 — Emergency',       desc: 'General emergency declared. Track logged and analyst notified.' },
                  { code: 'AIS',  label: 'AIS Blackout',                  desc: 'Vessel AIS transmission gap exceeds threshold — dark vessel risk escalated.' },
                  { code: 'ALT',  label: 'Unusual Altitude Profile',      desc: 'Rapid altitude changes or sub-100ft flight paths flagged for review.' },
                ].map((item) => (
                  <li key={item.code} className="flex items-start gap-4">
                    <div className="w-10 h-6 flex-shrink-0 flex items-center justify-center rounded bg-red-500/10 border border-red-500/20">
                      <span className="text-red-400 text-[9px] font-mono font-bold">{item.code}</span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">{item.label}</div>
                      <div className="text-xs text-slate-500 leading-relaxed">{item.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Historical Replay ─────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <Badge className="mb-4 bg-blue-500/10 text-blue-400 border border-blue-500/25 text-xs uppercase tracking-widest">Historical Analysis</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">Replay & Export Historical Tracks</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Scrub back through historical flight and vessel tracks. Replay routes at any speed, compare paths against known smuggling corridors, and export data in KML, GeoJSON, or CSV for downstream tools.
              </p>
              <ul className="space-y-3">
                {[
                  'Timeline scrubber with variable replay speed',
                  'Up to 90 days of historical AIS and ADS-B track storage',
                  'Route comparison against known smuggling corridors',
                  'Export KML for Google Earth, GeoJSON for analysis tools',
                  'Dwell time analysis — time spent in sensitive zones',
                  'Port call history for vessels with AIS data',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
              className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-semibold text-white">Historical Track Replay</span>
                  <Badge className="ml-auto bg-green-500/10 text-green-400 border border-green-500/20 text-xs">LIVE REPLAY</Badge>
                </div>
              </div>
              <HistoricalReplaySVG />
              <p className="text-xs text-slate-600 text-center mt-3">Vessel track over 2.5 hours with 3 recorded positions — scrubber at 01:15:00</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Capabilities grid ─────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3">Complete Air & Maritime Intelligence</h2>
            <p className="text-slate-400 max-w-xl mx-auto">From live positions to historical analysis, military detection to dark vessel alerts.</p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Live Air Traffic',      desc: '20K+ aircraft tracked in real time via OpenSky Network and ADS-B Exchange with sub-2s latency.' },
              { title: 'Military Classification', desc: 'Automatic military vs civil classification using ICAO range, callsign patterns, and Mode-S analysis.' },
              { title: 'AIS Vessel Tracking',   desc: '200K+ vessels via AISStream WebSocket. Filter by type, flag, MMSI, or risk score.' },
              { title: 'Dark Vessel Detection', desc: 'AIS gap analysis detects vessels going dark — automatic HIGH risk flag and analyst alert.' },
              { title: 'Squawk Monitoring',     desc: '7500/7600/7700 squawk codes trigger instant alerts with enriched context and position data.' },
              { title: 'Historical Replay',     desc: '90-day track history with timeline scrubber. Export KML, GeoJSON, CSV for any track or vessel.' },
            ].map((f) => (
              <div key={f.title} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 hover:border-green-500/20 transition-colors">
                <div className="text-sm font-semibold text-white mb-2">{f.title}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-24 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-4xl font-bold text-white mb-4">Start Tracking Now</h2>
            <p className="text-slate-400 text-lg mb-10 leading-relaxed">
              Full global air traffic and maritime surveillance. Military detection, dark vessel alerts, and historical analysis — all in one platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-10 shadow-lg shadow-green-500/40">
                  Get Started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/app/air-monitoring">
                <Button size="lg" variant="outline" className="border-white/[0.15] text-slate-300 hover:text-white hover:bg-white/[0.05]">
                  Open Air Monitoring
                  <Plane className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <ProductPageFooter />
    </div>
  )
}
